// Floating per-view control toolbar pinned to the top-center of the
// snapshot views. Built on base-ui's Toolbar primitives so the
// composition gets ARIA + keyboard-arrow navigation for free, while
// the icon glyphs / hover labels keep the chrome compact.

import { Toolbar } from '@base-ui/react/toolbar';
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';

import { Select } from '../../components/Select';
import selectStyles from '../../components/Select.module.css';

interface FloatingToolbarProps {
  children: ReactNode;
}

export function FloatingToolbar({ children }: FloatingToolbarProps) {
  return (
    <div className='snap-floating-toolbar'>
      <Toolbar.Root className='snap-toolbar' aria-label='View controls'>
        {children}
      </Toolbar.Root>
    </div>
  );
}

/**
 * Icon-only toggle inside the floating toolbar. Uses `aria-pressed` for
 * the toggle state and the native `title` attribute so the full label
 * surfaces on hover (per the spec: "show the full label on hover").
 */
export function ToolbarToggle({
  label,
  pressed,
  onToggle,
  children,
}: {
  label: string;
  pressed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <Toolbar.Button
      className={`snap-toolbar__toggle${pressed ? ' is-on' : ''}`}
      aria-pressed={pressed}
      onClick={onToggle}
      title={label}
      aria-label={label}
    >
      {children}
    </Toolbar.Button>
  );
}

/**
 * Labelled select inside the toolbar. Native `<select>` so behaviour
 * stays predictable across browsers; the label is visually small but
 * present for screen readers.
 */
export function ToolbarSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <Select
      label={label}
      title={label}
      value={value}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value as T)}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Select>
  );
}

/**
 * Native date input wearing the shared toolbar-pill chrome (reuses the
 * Select module's `.select` class, minus its chevron since the date input
 * carries its own calendar indicator). Value is a `YYYY-MM-DD` string;
 * empty string clears it. `max` bounds the pickable range (e.g. "today").
 */
export function ToolbarDate({
  label,
  value,
  onChange,
  max,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  max?: string;
}) {
  return (
    <input
      type='date'
      className={selectStyles.select}
      style={{ backgroundImage: 'none', paddingRight: 10, cursor: 'text' }}
      aria-label={label}
      title={label}
      value={value}
      max={max}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  );
}

/**
 * Multi-select dropdown inside the toolbar. The trigger shows a summary
 * ("All teams" / one label / "N selected"); the popover lists checkboxes
 * plus an "All" reset. An empty selection means "all". Built as a small
 * custom popover (the native `<select multiple>` is poor UX in a toolbar).
 */
export function ToolbarMultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel = 'All',
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? '1 selected')
        : `${selected.length} selected`;

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div ref={ref} className='snap-toolbar__multiselect'>
      <button
        type='button'
        className={selectStyles.select}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        {summary}
      </button>
      {open ? (
        <div className='snap-toolbar__multiselect-panel' role='listbox' aria-label={label}>
          <button
            type='button'
            className='snap-toolbar__multiselect-item'
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
          >
            {allLabel}
          </button>
          {options.map((o) => (
            <label key={o.value} className='snap-toolbar__multiselect-item'>
              <input
                type='checkbox'
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Text-labelled primary-action button inside the floating toolbar
 * (e.g. "Save"). Distinct from `ToolbarToggle` which is icon-only +
 * stateful; this one is a one-shot click with a visible label.
 */
export function ToolbarButton({
  label,
  onClick,
  disabled,
  variant = 'default',
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Visual treatment. `'default'` = primary-fill (Save / Update),
   *  `'danger'` = destructive red (Delete), `'ghost'` = neutral
   *  outline (Cancel). */
  variant?: 'default' | 'danger' | 'ghost';
}) {
  const className =
    variant === 'danger'
      ? 'snap-toolbar__btn snap-toolbar__btn--danger'
      : variant === 'ghost'
        ? 'snap-toolbar__btn snap-toolbar__btn--ghost'
        : 'snap-toolbar__btn';
  return (
    <Toolbar.Button
      className={className}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Toolbar.Button>
  );
}

/**
 * Icon-only one-shot button (distinct from `ToolbarToggle` which is
 * stateful via `aria-pressed`). Used for the Timeline scrubber's
 * Prev/Next-Highlight controls — pure navigation, no on/off state.
 * Disabled when there's nothing to navigate to (no prior/next highlight).
 */
export function ToolbarIconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Toolbar.Button
      className='snap-toolbar__toggle'
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      {children}
    </Toolbar.Button>
  );
}

/** Vertical divider between toolbar sections. */
export function ToolbarSeparator() {
  return <Toolbar.Separator className='snap-toolbar__separator' />;
}

/**
 * Segmented control inside the toolbar — N mutually-exclusive options
 * rendered as adjacent pill buttons. Used by the Roadmap view's
 * Resource / Impact / Diff switch. `value` is the selected option;
 * clicking a different segment fires `onChange`.
 */
export function ToolbarSegmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div
      className='snap-toolbar__segmented'
      role='group'
      aria-label={label}
    >
      {options.map(opt => (
        <Toolbar.Button
          key={opt.value}
          className={
            value === opt.value
              ? 'snap-toolbar__segmented-btn is-on'
              : 'snap-toolbar__segmented-btn'
          }
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Toolbar.Button>
      ))}
    </div>
  );
}

// ---- Icons ---------------------------------------------------------

/** Group Skills toggle icon (Lucide `layers`). */
/**
 * Facet grounding: a hub with edges converging on it — the shared facet and the
 * business functions the dependency graph says consume it. Deliberately not a
 * layers/stack glyph, which already means "grouping" in this toolbar.
 *
 * `dimmed` renders it at reduced opacity for an org with no facet rules, where the
 * control is present but inert.
 */
/**
 * Ultra-compact rows: three stacked rules squeezed toward a centre line, with
 * arrows pressing in from top and bottom — "collapse the vertical". Reads as
 * compression rather than grouping, so it doesn't collide with the layers
 * glyph that already means "group" in this toolbar.
 */
export function UltraCompactIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      {/* Arrows pressing inward */}
      <path d='M12 2.5v3.5M9.75 4.25 12 2 14.25 4.25' />
      <path d='M12 21.5v-3.5M9.75 19.75 12 22 14.25 19.75' />
      {/* The squeezed rows */}
      <path d='M4 9h16' />
      <path d='M4 12h16' />
      <path d='M4 15h16' />
    </svg>
  );
}

/**
 * Debug cell-scaling: three nested squares, largest to smallest, reading as
 * "same cell, different magnitude". Distinct from the layers glyph, which
 * already means grouping here.
 */
export function CellScaleIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinejoin='round'
      aria-hidden
    >
      <rect x='3' y='3' width='8' height='8' rx='1' />
      <rect x='14.5' y='4.5' width='5' height='5' rx='1' />
      <rect x='5.5' y='15.5' width='5' height='5' rx='1' />
      <rect x='16' y='16' width='2.5' height='2.5' rx='0.5' />
    </svg>
  );
}

export function FacetGroundingIcon({ dimmed = false }: { dimmed?: boolean }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
      style={dimmed ? { opacity: 0.4 } : undefined}
    >
      <circle cx='12' cy='12' r='2.5' />
      <circle cx='4.5' cy='5' r='1.75' />
      <circle cx='19.5' cy='5' r='1.75' />
      <circle cx='4.5' cy='19' r='1.75' />
      <circle cx='19.5' cy='19' r='1.75' />
      <path d='M6 6.3 10.2 10.4' />
      <path d='M18 6.3 13.8 10.4' />
      <path d='M6 17.7 10.2 13.6' />
      <path d='M18 17.7 13.8 13.6' />
    </svg>
  );
}

export function GroupSkillsIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z' />
      <path d='M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12' />
      <path d='M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17' />
    </svg>
  );
}

/** Show Libraries toggle icon (Lucide `library`). */
export function ShowLibrariesIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='m16 6 4 14' />
      <path d='M12 6v14' />
      <path d='M8 8v12' />
      <path d='M4 4v16' />
    </svg>
  );
}

/** Show Departed toggle icon (Lucide `user-x`). */
export function ShowDepartedIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M18 21a8 8 0 0 0-16 0' />
      <circle cx='10' cy='8' r='5' />
      <path d='m17 3 5 5' />
      <path d='m22 3-5 5' />
    </svg>
  );
}

/** Prev-Highlight icon (Lucide `chevrons-left`). */
export function PrevHighlightIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='m11 17-5-5 5-5' />
      <path d='m18 17-5-5 5-5' />
    </svg>
  );
}

/** Next-Highlight icon (Lucide `chevrons-right`). */
export function NextHighlightIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='m6 17 5-5-5-5' />
      <path d='m13 17 5-5-5-5' />
    </svg>
  );
}

